import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  getGetChronologyEventsQueryKey,
  useConfirmChronologyEvent,
  useCreateChronologyEvent,
  useDeleteChronologyEvent,
  useGetChronologyEvents,
  useResetChronologyEvent,
  useUpdateChronologyEvent,
} from "~/api/chronology/chronology";
import type {
  ChronologyEvent,
  CreateChronologyEventRequest,
  UpdateChronologyEventRequest,
} from "~/api/model";
import { useChatDocumentParams } from "~/helpers";
import { sortChronologyEvents } from "../helpers/sortChronologyEvents";

/**
 * Fetches the case chronology and returns the events already sorted for
 * display (dated ascending, undated last).
 */
export const useChronologyEvents = () => {
  const { organizationId, caseId } = useChatDocumentParams();

  const { data, isPending, isFetching, isError, error, refetch } =
    useGetChronologyEvents(organizationId, caseId, {
      query: {
        refetchOnWindowFocus: false,
      },
    });

  // React Query keeps `data` referentially stable between renders unless it
  // changes, so memoizing here gives consumers a stable `events` reference and
  // keeps downstream memoization (e.g. duplicate detection) from rerunning on
  // every unrelated render.
  const events = useMemo(
    () => (data ? sortChronologyEvents(data.events) : []),
    [data]
  );

  return {
    events,
    isLoading: isPending,
    isFetching,
    isError,
    error,
    refetch,
  };
};

/**
 * Wraps the generated chronology mutations and invalidates the list query
 * after each one so the UI always reflects the latest server state.
 */
export const useChronologyMutations = () => {
  const { organizationId, caseId } = useChatDocumentParams();
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getGetChronologyEventsQueryKey(organizationId, caseId),
      }),
    [queryClient, organizationId, caseId]
  );

  const { mutateAsync: createEvent, isPending: isCreating } =
    useCreateChronologyEvent();
  const { mutateAsync: updateEvent, isPending: isUpdating } =
    useUpdateChronologyEvent();
  const { mutateAsync: deleteEvent, isPending: isDeleting } =
    useDeleteChronologyEvent();
  const { mutateAsync: resetEvent, isPending: isResetting } =
    useResetChronologyEvent();
  const { mutateAsync: confirmEvent, isPending: isConfirming } =
    useConfirmChronologyEvent();

  const create = useCallback(
    async (data: CreateChronologyEventRequest): Promise<ChronologyEvent> => {
      const result = await createEvent({
        organizationId,
        projectId: caseId,
        data,
      });
      await invalidate();
      return result;
    },
    [createEvent, invalidate, organizationId, caseId]
  );

  const update = useCallback(
    async (
      eventId: string,
      data: UpdateChronologyEventRequest
    ): Promise<ChronologyEvent> => {
      const result = await updateEvent({
        organizationId,
        projectId: caseId,
        eventId,
        data,
      });
      await invalidate();
      return result;
    },
    [updateEvent, invalidate, organizationId, caseId]
  );

  const remove = useCallback(
    async (eventId: string): Promise<void> => {
      await deleteEvent({ organizationId, projectId: caseId, eventId });
      await invalidate();
    },
    [deleteEvent, invalidate, organizationId, caseId]
  );

  const reset = useCallback(
    async (eventId: string): Promise<ChronologyEvent> => {
      const result = await resetEvent({
        organizationId,
        projectId: caseId,
        eventId,
      });
      await invalidate();
      return result;
    },
    [resetEvent, invalidate, organizationId, caseId]
  );

  const confirm = useCallback(
    async (eventId: string): Promise<ChronologyEvent> => {
      const result = await confirmEvent({
        organizationId,
        projectId: caseId,
        eventId,
      });
      await invalidate();
      return result;
    },
    [confirmEvent, invalidate, organizationId, caseId]
  );

  return {
    create,
    update,
    remove,
    reset,
    confirm,
    isCreating,
    isUpdating,
    isDeleting,
    isResetting,
    isConfirming,
  };
};
